import clsx from 'clsx';
import { useMemo } from 'react';
import { getSchemaPrice } from '~/shared/utils/schema-price';
import { PriceTypes, TPrice } from '~/shared/components/ProductCard/components/Price';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { useUtils } from '~/shared/hooks/useUtils';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import styles from './ProductPrices.module.scss';
import { PromotionType } from '~/lib/data-contract/salesforce';
import { SalesforceProductSpecialPrice } from '~/lib/salesforce/types';
import { useSpecialPrice } from '~/services/special-price/useSpecialPrice';
import { shouldHighlightDefaultPromoPrice } from '~/shared/utils/shouldHighlightDefaultPromoPrice';
import { derivePromoType } from '~/lib/salesforce/utilities/prices';

export interface ProductPricesProps {
    id: string;
    priceList: TPrice[] | undefined;
    customerGroup: string | undefined;
    minPrice: number | undefined;
    specialPrices: SalesforceProductSpecialPrice | undefined;
    promoType: string | undefined;
}

export const extractProductPrices = (product: TProductDetails): ProductPricesProps => ({
    id: product.id,
    priceList: product.c_prices ?? product.cust_prices,
    customerGroup: product.cust_group,
    minPrice: product.cust_minimumPrice,
    specialPrices: product.cust_specialPrices,
    promoType: product.cust_promotionType,
});

type Props = ProductPricesProps &
    React.HTMLAttributes<HTMLDivElement> & {
        isCondensed?: boolean;
    };

export const ProductPrices: React.FC<Props> = ({
    className,
    priceList,
    customerGroup,
    minPrice,
    specialPrices,
    id,
    promoType,
    isCondensed,
    ...rest
}) => {
    const { data: frame } = useFrame();
    const currency = frame.market.currency?.currency || 'DKK';

    const hasGiftCard = customerGroup?.includes('giftcard');
    const prices = useMemo(() => {
        if (hasGiftCard) {
            return <GiftCardPrice price={minPrice} />;
        }

        if (specialPrices) {
            return (
                <SpecialPrice
                    productId={id}
                    prices={priceList ?? []}
                    specialPrices={specialPrices}
                />
            );
        }

        if (priceList?.length) return <Prices prices={priceList} promoType={promoType} />;

        return null;
    }, [customerGroup, specialPrices?.id, id, priceList]);

    return (
        <div className={clsx(styles.prices, isCondensed && styles.condensed, className)} {...rest}>
            <meta itemProp="priceCurrency" content={currency} />

            {prices}
        </div>
    );
};

interface PriceProps {
    label?: string;
    price: string | number | undefined;
    type: PriceTypes;
}

const Price: React.FC<PriceProps> = ({ label, price, type }) => {
    const { convertPriceToLocale } = useUtils();

    return (
        <p className={clsx(styles.price, styles[type])}>
            {/* An element rather than a bare text node: an anonymous flex or grid item cannot
                be targeted or placed, so the condensed layout has nothing to grab otherwise. */}
            {label && <span className={styles.label}>{label} </span>}

            <span className={styles.emphasized}>{convertPriceToLocale(price)}</span>
        </p>
    );
};

const GiftCardPrice: React.FC<{
    price: number | undefined;
}> = ({ price = 0 }) => {
    const { translate } = useTranslation();

    return (
        <>
            <meta itemProp="price" content={String(price)} />
            <Price type={PriceTypes.DEFAULT} label={translate('common.from')} price={price} />
        </>
    );
};

const SpecialPrice: React.FC<{
    productId: string;
    prices: TPrice[];
    specialPrices: SalesforceProductSpecialPrice;
}> = ({ productId, prices, specialPrices }) => {
    const { prices: priceList = [] } = useSpecialPrice({
        id: productId,
        c_specialPrices: specialPrices,
        c_prices: prices,
    });

    return <Prices prices={priceList} />;
};

const Prices: React.FC<{ prices: TPrice[]; promoType?: string; splash?: { text?: string } }> = ({
    prices,
    promoType,
    splash,
}) => {
    const { translate } = useTranslation();
    // We will move the default prices to the end of the price list.
    const sortedPrices = useMemo(
        () => prices?.sort((a) => (a.type === PriceTypes.SALE ? -1 : 0)),
        [prices],
    );

    const hasMemberPrice = prices.some((price) => price.promotype === PromotionType.MEMBER_PRICE);
    const schemaPrice = getSchemaPrice(prices);

    return (
        <>
            {schemaPrice && <meta itemProp="price" content={String(schemaPrice.value)} />}

            {sortedPrices?.map((price) => {
                const priceType = price.type || PriceTypes.DEFAULT;
                const resolvedPromoType = derivePromoType({ promoType, splash, prices });

                // Highlight single default Christmas/Super rows as sale; keep dual-price "before" gray.
                const shouldHighlightDefaultPrice = shouldHighlightDefaultPromoPrice({
                    priceType,
                    promoType: resolvedPromoType,
                    prices,
                });

                const displayType = shouldHighlightDefaultPrice ? PriceTypes.SALE : priceType;

                // We will display a different label for non-member price (default) on membership prices.
                const displayLabel =
                    hasMemberPrice && prices.length > 1
                        ? price.promotype === PromotionType.MEMBER_PRICE
                            ? translate('pdp.memberPrice')
                            : translate('pdp.notMemberPrice')
                        : price.label;

                return (
                    <Price
                        key={priceType}
                        type={displayType}
                        label={displayLabel}
                        price={price.value}
                    />
                );
            })}
        </>
    );
};
